class CustomProductHighlight extends HTMLElement {
  constructor() {
    super();
    this.currentProductIndex = 0;
    this.productData = [];
  }

  connectedCallback() {
    this.initializeProductData();
    this.setupEventListeners();
    this.updateProductDisplay(0);
  }

  initializeProductData() {
    // Get all product blocks from the section
    const sectionId = this.dataset.sectionId;
    const thumbnailButtons = this.querySelectorAll('[data-product-id]');
    
    thumbnailButtons.forEach((btn) => {
      const productIndex = btn.getAttribute('data-product-id');
      this.productData.push({
        id: productIndex,
        element: btn,
      });
    });
  }

  setupEventListeners() {
    // Thumbnail click handlers
    const thumbnailButtons = this.querySelectorAll('.thumbnail-list__item button');
    thumbnailButtons.forEach((btn, index) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.updateProductDisplay(index);
      });
    });

    // Navigation buttons - Use closest to find the element since they're outside the custom element
    const container = this.closest('section') || document;
    const prevBtn = container.querySelector('#cph-prev-btn');
    const nextBtn = container.querySelector('#cph-next-btn');

    if (prevBtn) {
        prevBtn.addEventListener('click', () => this.previousProduct());
        console.log('Previous button found and listener added');
    }

    if (nextBtn) {
        nextBtn.addEventListener('click', () => this.nextProduct());
        console.log('Next button found and listener added');
    }

    // View Product link - Update href on product change
    const viewProductLink = this.querySelector('.add-to-cart');
    if (viewProductLink) {
      // Don't add click listener since it's a link, just update href dynamically
    }
  }

  updateProductDisplay(index) {
    // Validate index
    const totalProducts = this.querySelectorAll('li[data-product-index]').length || this.querySelectorAll('[data-product-index]').length;
    if (index < 0 || index >= totalProducts) {
      return;
    }

    this.currentProductIndex = index;

    // Update main image with fade animation
    const mainImg = this.querySelector('#cph-main-img');
    const productInfo = this.querySelector('#cph-product-info');
    const addToCartBtn = this.querySelector('.add-to-cart');

    if (mainImg && productInfo) {
      // Add fade class for animation
      mainImg.classList.remove('cph-fade-transition');
      productInfo.classList.remove('cph-fade-transition');
      
      // Trigger reflow to restart animation
      void mainImg.offsetWidth;
      void productInfo.offsetWidth;

      mainImg.classList.add('cph-fade-transition');
      productInfo.classList.add('cph-fade-transition');

      // Update image source from data attribute
      let thumbnailBtn = this.querySelector(`button[data-product-index="${index}"]`);
      if (!thumbnailBtn) {
        const anyEl = this.querySelector(`[data-product-index="${index}"]`);
        if (anyEl) thumbnailBtn = anyEl.tagName === 'BUTTON' ? anyEl : anyEl.querySelector('button');
      }
      if (thumbnailBtn) {
        const imageUrl = thumbnailBtn.getAttribute('data-product-image') || thumbnailBtn.querySelector('img')?.src;
        const titleForAlt = thumbnailBtn.getAttribute('data-product-title') || thumbnailBtn.querySelector('img')?.alt;
        if (imageUrl) {
          mainImg.src = imageUrl;
          mainImg.srcset = this.generateSrcset(imageUrl);
          if (titleForAlt) mainImg.alt = titleForAlt;
        }
      }
    }

    // Update thumbnail active state
    this.updateThumbnailActive(index);

    // Update product info (title, price, description, etc.)
    this.updateProductInfo(index);
  }

  updateThumbnailActive(index) {
    // Remove active class from all thumbnails
    const allThumbs = this.querySelectorAll('.cph-thumb');
    allThumbs.forEach((thumb) => {
      thumb.classList.remove('cph-thumbnail-active');
      const parentBtn = thumb.closest('button');
      if (parentBtn) {
        parentBtn.setAttribute('aria-current', 'false');
      }
    });

    // Add active class to selected thumbnail
    if (allThumbs[index]) {
      allThumbs[index].classList.add('cph-thumbnail-active');
      const parentBtn = allThumbs[index].closest('button');
      if (parentBtn) {
        parentBtn.setAttribute('aria-current', 'true');
      }
    }
  }

  updateProductInfo(index) {
    // Extract product data from the thumbnail button's data attributes
    const productInfo = this.querySelector('#cph-product-info');
    
    if (productInfo) {
      let thumbnailBtn = this.querySelector(`button[data-product-index="${index}"]`);
      if (!thumbnailBtn) {
        const anyEl = this.querySelector(`[data-product-index="${index}"]`);
        if (anyEl) thumbnailBtn = anyEl.tagName === 'BUTTON' ? anyEl : anyEl.querySelector('button');
      }
      if (thumbnailBtn) {
        const title = thumbnailBtn.getAttribute('data-product-title');
        const price = thumbnailBtn.getAttribute('data-product-price');
        const comparePrice = thumbnailBtn.getAttribute('data-product-compare');
        const url = thumbnailBtn.getAttribute('data-product-url');
        const description = thumbnailBtn.getAttribute('data-product-description');
        const productId = thumbnailBtn.getAttribute('data-product-id');

        // Update title
        const titleLink = productInfo.querySelector('.card-title a');
        
        if (titleLink) {
          titleLink.textContent = title;
          titleLink.href = url;
        }

        // Update price
        const priceEl = productInfo.querySelector('.card-pricing');
        
        if (priceEl && price) {
          let priceHtml = price;
          if (comparePrice && comparePrice !== price) {
            priceHtml += ` <span class="discount-tag">${comparePrice}</span>`;
          }
          priceEl.innerHTML = priceHtml;
        }

        // Update description
        const descEl = productInfo.querySelector('.card-description');
        
        if (descEl && description) {
          descEl.textContent = description;
        }

        // Update View Product link
        const viewProductLink = this.querySelector('.add-to-cart');
        if (viewProductLink && url) {
          viewProductLink.setAttribute('href', url);
          viewProductLink.setAttribute('data-product-url', url);
        }
      }
    }
  }

  generateSrcset(largeSrc) {
    // Generate responsive srcset based on the large source
    const sizes = ['400x400', '600x600', '800x800'];
    return sizes
      .map((size) => {
        const src = largeSrc.replace('800x800', size);
        const width = parseInt(size.split('x')[0]);
        return `${src} ${width}w`;
      })
      .join(', ');
  }

  previousProduct() {
    let newIndex = this.currentProductIndex - 1;
    const totalProducts = this.querySelectorAll('li[data-product-index]').length || this.querySelectorAll('[data-product-index]').length;
    
    if (newIndex < 0) {
      newIndex = totalProducts - 1;
    }
    
    this.updateProductDisplay(newIndex);
  }

  nextProduct() {
    let newIndex = this.currentProductIndex + 1;
    const totalProducts = this.querySelectorAll('li[data-product-index]').length || this.querySelectorAll('[data-product-index]').length;
    
    if (newIndex >= totalProducts) {
      newIndex = 0;
    }
    
    this.updateProductDisplay(newIndex);
  }

  viewProduct() {
    const productUrl = this.querySelector('.add-to-cart').getAttribute('data-product-url');
    
    if (!productUrl) {
      console.warn('Product URL not found');
      return;
    }

    // Navigate to product page
    window.location.href = productUrl;
  }
}

customElements.define('custom-product-highlight', CustomProductHighlight);
